import React from 'react';
import {
	useIntl,
} from 'react-intl';

import AbbreviatedNumber from '~/components/app/AbbreviatedNumber';
import AttachedChartLegend, {
	AttachedChartLegendAlignment,
} from '~/components/patterns/charts/structures/AttachedChartLegend';
import ChartContainer from '~/components/atoms/charts/components/ChartContainer';
import ChartLegend from '~/components/patterns/charts/components/ChartLegend';
import DonutChart from '~/components/atoms/charts/charts/DonutChart';

import useViewportType from '~/hooks/useViewportType';



const defaultRandomColors = [];



type Props<Value extends string | number | symbol | boolean> = {
	categories?: Array<{
		value: any;
		label: string;
		color: string;
	}>;
	data?: Array<{
		count: number;
		percentage: number;
		value: Value;
	}>;
	height?: number;
	name: string;
	percentageFactor?: number;
	randomColors?: ReadonlyArray<string>;
};

function DonutPagesChart<Value extends string | number | symbol | boolean>(props: Props<Value>) {
	const {
		categories,
		data = [],
		height,
		name,
		percentageFactor = 1,
		randomColors = defaultRandomColors,
	} = props;

	const intl = useIntl();
	const viewportType = useViewportType();

	function renderLegend() {
		const items: Array<{
			color: string | undefined,
			count: number,
			countDisplay: React.ReactNode,
			label: string,
		}> = [];

		let usedSpecificColors = 0;

		if (categories) {
			categories.forEach((category) => {
				const hasData = data.length > 0;

				const count = data.find((item) => item.value === category.value)?.count ?? 0;

				items.push({
					color: category.color,
					count,
					countDisplay: hasData ? <AbbreviatedNumber value={count} /> : '...',
					label: category.label,
				});
			});
		} else {
			if (!data.length) {
				items.push({
					color: 'rgba(0,0,0,0.2)',
					count: 0,
					countDisplay: ' ',
					label: '...',
				});
			}

			data.forEach((item, index: number) => {
				const value = item.value;
				const color = randomColors[index - usedSpecificColors] ?? 'grey';
				usedSpecificColors += 1;

				const count = item.count;

				items.push({
					color,
					count,
					countDisplay: <AbbreviatedNumber value={count} />,
					label: String(value),
				});
			});
		}

		return (
			<ChartLegend
				items={items}
			/>
		);
	}

	const renderTooltip = React.useCallback(
		(context: Highcharts.TooltipFormatterContextObject) => {
			const category = categories?.[context.point.index];
			const label = category?.label ?? String(context.key);
			const item = data.find((item) => item.value === category?.value)

			const count = item?.count ?? context.y ?? NaN;
			const percentage = item?.percentage ?? context.percentage;

			const formattedCount = intl.formatNumber(count);
			const formattedPercentage = intl.formatNumber(
				percentage / percentageFactor,
				{
					style: 'percent',
					maximumFractionDigits: 2,
					maximumSignificantDigits: 4,
				},
			);

			return `${label}: ${formattedCount} (${formattedPercentage})`;
		},
		[
			data,
			intl,
			categories,
			percentageFactor,
		],
	);

	let usedSpecificColors = 0;
	const chartData = data
		.filter((item) => item.count > 0)
		.sort((itemA, itemB) => {
			if (categories) {
				const positionA = categories.findIndex((cat) => cat.value === itemA.value);
				const positionB = categories.findIndex((cat) => cat.value === itemB.value);

				if (positionA === positionB) {
					return 0;
				}

				return positionA < positionB ? -1 : 1;
			}

			return 0;
		})
		.map((item, index) => {
			const category = categories?.find((cat) => cat.value === item.value);
			let color = category?.color;

			if (!color) {
				color = randomColors[index - usedSpecificColors] ?? 'grey';
				usedSpecificColors++;
			}

			return {
				name: String(item.value),
				y: item.count,
				color,
			};
		});

	return (
		<AttachedChartLegend
			legend={renderLegend()}
			legendAlignment={AttachedChartLegendAlignment.Right}
		>
			<ChartContainer
				chart={(
					<DonutChart
						animate={true}
						data={[
							{
								data: chartData,
							},
						]}
						tooltipFormatter={renderTooltip}
						viewportType={viewportType}
					/>
				)}
				height={height}
				name={name}
				type="donut-chart"
			/>
		</AttachedChartLegend>
	);
}



export default DonutPagesChart;
